import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TrackByFunction,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Omnibox } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { DatePickerService } from '@shared/services/date-picker.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { eventToKeys, isEnterKey, isPrintableKey, isSingleKey, KeyName } from '@local/ts-infra';
import { Logger } from '@unleash-tech/js-logger';
import scrollIntoView from 'scroll-into-view-if-needed';
import { InputAutocompletePipe } from './input-autocomplete.pipe';
import { SuggestionsDropdownItemComponent } from './suggestions-dropdown-item.component';
import { DateValue } from '@local/common-web';

@UntilDestroy()
@Component({
  selector: 'suggestions-dropdown',
  templateUrl: './suggestions-dropdown.component.html',
  styleUrls: ['./suggestions-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuggestionsDropdownComponent implements OnInit, OnDestroy {
  model: Omnibox.Suggestion[];

  private _provider: (term: string, group: string) => Promise<Omnibox.Suggestion[]>;

  @Output() groupSelected: EventEmitter<string> = new EventEmitter<string>();

  get provider(): (term: string, group: string) => Promise<Omnibox.Suggestion[]> {
    return this._provider;
  }

  @Input() set provider(s: (term: string, group: string) => Promise<Omnibox.Suggestion[]>) {
    this._provider = s;
  }

  @Input() set show(value: boolean) {
    if (value) {
      this.keysHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 10);
      this.load();
    } else {
      if (this.keysHandlerId) this.keyboardService.unregisterKeyHandler(this.keysHandlerId);
      this.query = '';
      this.group = null;
    }

    this.resetState();
    this.visible = value;
    this.cdr.markForCheck();
  }

  @Output() onSelect: EventEmitter<Omnibox.Suggestion> = new EventEmitter();
  @Output() onBoxInput: EventEmitter<string> = new EventEmitter();
  @Output() close: EventEmitter<KeyName> = new EventEmitter();
  @Output() datePickerVisible: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChildren('option', { read: ElementRef }) optionsRef: QueryList<ElementRef>;
  @ViewChildren('option') optionsComp: QueryList<SuggestionsDropdownItemComponent>;
  @ViewChild('input') inputRef: ElementRef;
  @ViewChild('list') listRef: ElementRef;
  @ViewChild('container') dropdownContainer: ElementRef;

  visible: boolean;
  query: string = '';
  _group: Omnibox.Suggestion = null;
  placeholderValue: string;
  datePickerCustomValue: DateValue;

  set group(g: Omnibox.Suggestion) {
    this._group = g;
  }

  get group() {
    return this._group;
  }
  private _selectedIndex: number;
  private logger: Logger;
  private keysHandlerId: string;

  get selectedIndex(): number {
    return this._selectedIndex;
  }

  set selectedIndex(i: number) {
    this._selectedIndex = i;
    this.cdr.markForCheck();
  }

  get selected(): Omnibox.Suggestion {
    if (this._selectedIndex == null || !this.model) {
      return;
    }
    return this.model[this._selectedIndex];
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private eventsService: EventsService,
    logService: LogService,
    private keyboardService: KeyboardService,
    private InputAutocompletePipe: InputAutocompletePipe,
    private datePickerService: DatePickerService
  ) {
    this.logger = logService.scope('SuggestionDropDownComponent');
  }

  async ngOnInit() {
    this._selectedIndex = 0;
  }

  ngOnDestroy(): void {
    if (this.keysHandlerId) this.keyboardService.unregisterKeyHandler(this.keysHandlerId);
  }

  async applySelected() {
    // +1 since we need to take the search item into account
    const selected = this.optionsComp.get(this.selectedIndex + 1)?.model;
    if (selected) {
      this._selectedIndex = 0;
      this.clearPlaceholder();
      this.query = '';
      if (selected.type == 'group') {
        this.groupSelected.emit(selected.id);

        this.group = selected;
        this.load().then(() => this.scrollToTop());

        return;
      }
      if (selected.picker === 'time-dropdown' && selected.title.toLowerCase().startsWith('custom')) {
        const { x, y } = this.dropdownContainer.nativeElement.getBoundingClientRect();
        this.datePickerService.openPopup({ x, y: y - 34 }, selected.label);
        this.datePickerVisible.emit(true);
        this.subscribeToDatePickerClosed(selected);
        this.subscribeToDatePickerValueSelected();
        return;
      }
      this.onSelect.emit(selected);
      this.resetState();
    }
  }

  subscribeToDatePickerValueSelected() {
    this.datePickerService.valueSelected$.pipe(untilDestroyed(this)).subscribe((v) => {
      this.datePickerCustomValue = v;
    });
  }

  subscribeToDatePickerClosed(selected) {
    this.datePickerService.close$.pipe(untilDestroyed(this)).subscribe((c) => {
      const customDateValue = this.datePickerService.buildSelectedTimeEventValue(this.datePickerCustomValue);
      if (customDateValue) {
        const selectedValue = {
          ...selected,
          id: `${selected.id.replace(':Custom...', '')}:${customDateValue}`,
          data: { ...selected.data, value: `${this.datePickerCustomValue.type}:${customDateValue}` },
          title: customDateValue[0],
        };
        this.onSelect.emit(selectedValue);
      }
    });
  }

  private clearPlaceholder() {
    this.placeholderValue = null;
    this.cdr.markForCheck();
  }

  private qid = 0;

  private async load() {
    if (!this.provider) {
      this.model = [];
      this.cdr.markForCheck();
      return;
    }
    let qd = ++this.qid;
    let res = await this.provider(this.query, this.group?.id);
    if (qd != this.qid) return;
    this.model = res;
    this._selectedIndex = 0;
    this.cdr.markForCheck();
  }

  async onInput() {
    if (this.query) this.updateAutoComplete();
    else this.placeholderValue = null;
    await this.load();
    this.reportSearchTelemetry(this.query);
    if (this.query.charAt(this.query.length) !== ' ') {
      this.select('first');
      this.updateAutoComplete();
      this.scrollToTop();
    }
    this.onBoxInput.emit(this.query);
  }

  get defaultPlaceholder() {
    return this.group?.title ? 'Filter by ' + this.group?.title : 'Search for...';
  }

  private scrollToTop() {
    this.listRef?.nativeElement.scroll(0, 0);
  }

  updateAutoComplete() {
    this.placeholderValue = this.buildAutoComplete();
    this.cdr.markForCheck();
  }

  buildAutoComplete() {
    if (this.query == '' && this.model?.length === 0) {
      return; //no result logic
    }

    const selected = this.selected;
    if (!selected) return;

    let selectedTitle = selected.title;
    let prefix = this.selected.type === 'group' && selected.id === 'group:page' ? 'Filter By' : null;

    if (
      !!this.query &&
      (this.group ||
        !this.valueSuggestion(selected) ||
        selected.label !== 'label' ||
        this.query.toLocaleLowerCase().includes(selected.label.toLocaleLowerCase()))
    ) {
      selectedTitle = selectedTitle.toLowerCase().split(':').slice(-1)[0].trim();
    }

    return this.InputAutocompletePipe.transform(this.query, selectedTitle, prefix);
  }

  private valueSuggestion(suggestion: Omnibox.Suggestion): boolean {
    return suggestion.type.includes('filter') && suggestion.id.includes(':value:');
  }

  private resetState() {
    this.selectedIndex = 0;
    this.scrollToTop();
    this.clearPlaceholder();
    this.cdr.markForCheck();
  }

  applyAutocomplete() {
    this.query = this.placeholderValue = this.optionsComp.find((i) => i.selected)?.model.title;
    this.onInput();
  }

  onInputKeyDown(event: KeyboardEvent) {
    const keys = eventToKeys(event);

    if ((isSingleKey('backspace', keys) || isSingleKey('delete', keys) || isSingleKey('escape', keys)) && !this.query) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();

      if (!this.clearGroup()) this.close.emit(keys[0]);

      return;
    }

    if (isSingleKey('tab', keys)) {
      event.preventDefault();
      this.applyAutocomplete();
      this.applySelected();
    }

    if (
      isSingleKey('ArrowLeft', keys) &&
      this.inputRef.nativeElement?.selectionEnd === 0 &&
      this.inputRef.nativeElement?.selectionStart === 0
    ) {
      this.clearGroup();
      event.stopPropagation();
    }
    if (isSingleKey('ArrowRight', keys)) {
      this.applySelected();
      event.stopPropagation();
    }
  }

  /** @return true if group cleared */
  clearGroup() {
    if (this.group) {
      this.group = null;
      this.clearText();
      this.clearPlaceholder();
      this.load();
      this.focusSearch();
      this.scrollToTop();
      return true;
    }
  }

  focusSearch() {
    setTimeout(() => {
      this.inputRef.nativeElement.focus();
    }, 0);
  }

  handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.visible || keys.length > 1) return;
    const key = keys[0];

    if (key === 'escape') {
      if (!this.clearGroup()) this.close.emit();
      event.stopPropagation();
      return;
    }
    if (key === 'ArrowDown') {
      this.select('next');
      this.updateAutoComplete();
      this.cdr.markForCheck();
      event.stopPropagation();
      return;
    } else if (key === 'ArrowUp') {
      this.select('prev');
      this.updateAutoComplete();
      this.cdr.markForCheck();
      event.stopPropagation();
      return;
    } else if (key === 'pageDown') {
      this.select('next', 5);
      this.updateAutoComplete();
      this.cdr.markForCheck();
      event.stopPropagation();
      return;
    } else if (key === 'pageUp') {
      this.select('prev', 5);
      this.updateAutoComplete();
      this.cdr.markForCheck();
      event.stopPropagation();
      return;
    } else if (isEnterKey(key)) {
      this.applySelected();
      event.stopPropagation();
      return;
    }
    if (isPrintableKey(event)) {
      event.stopPropagation();
    }
  }

  clearText() {
    this.query = '';
  }

  trackItems: TrackByFunction<Omnibox.Suggestion> = (index: number, item: Omnibox.Suggestion): string => item.id;

  select(value: 'next' | 'prev' | 'first' | number, skipAmount = 1): void {
    if (!this.model || this.model.length == 0) {
      this._selectedIndex = null;
      return;
    }

    if (Number.isInteger(value as any)) {
      const dx = value as number;
      if (dx < 0 || dx >= this.model.length) {
        return;
      }
      this._selectedIndex = dx;
    }

    let finalIndex: number;

    if (value === 'first' || this.selectedIndex === undefined) {
      finalIndex = 0;
    } else if (value === 'next') {
      finalIndex =
        this.selectedIndex + skipAmount > this.optionsRef.length - 2 ? this.optionsRef.length - 2 : this.selectedIndex + skipAmount;
    } else if (value === 'prev') {
      finalIndex = this.selectedIndex - skipAmount <= 0 ? 0 : this.selectedIndex - skipAmount;
    }

    this._selectedIndex = finalIndex;

    const elements = this.optionsRef.toArray();

    if (value === 'next' || (value === 'prev' && this.selectedIndex !== elements.length - 2)) {
      if (elements[finalIndex]) {
        let scrollTo = finalIndex + 1 + (value === 'prev' ? 0 : skipAmount);
        if (scrollTo <= 2) scrollTo = 1;
        if (scrollTo > elements.length) scrollTo = elements.length - 1;
        if (elements[scrollTo]) this.scrollTo(scrollTo);
      }
    }
    this.cdr.markForCheck();
  }

  private scrollTo(i: number) {
    const elements = this.optionsRef.toArray();

    scrollIntoView(elements[i].nativeElement, {
      scrollMode: 'always',
      block: 'nearest',
      inline: 'nearest',
      behavior: 'auto',
      boundary: <Element>elements[0].nativeElement,
    });
  }

  private reportSearchTelemetry(input: string) {
    const telemetryEvent: Partial<EventInfo> = {
      name: 'lookup',
      category: 'scopes',
      label: input,
    };

    this.eventsService.event('scopes', telemetryEvent);
  }
}
